import {curry} from 'ramda';
import wrap from 'wraptile';
import moment from 'moment';
import {QcJobTestStatuses} from '@pluto/@types';
import {QCFormattedStatusEnum} from '../../QCWorkflow/QCFilters/QCFiltersType';
import {QCJobItem} from '../../QCWorkflow/QCManager/QCManagerTypes';

export const COPY_RIGHT_START_YEAR = 1921;
export const COPY_RIGHT_END_YEAR = new Date().getFullYear();

export const getContentAnchorEl = () => (null as unknown) as Element;

export const changeEditMode = wrap(
    (setIsEditMode: (value: boolean) => void, editModeValue: boolean) => {
        setIsEditMode(editModeValue);
    }
);

export const isFailedStatus = (a: any) => a.includes('FAILED');

export const getValidTime = (val: number) => (val < 10 ? `0${val}` : val);
export const getValidMilliSeconds = (val: number) => (val < 100 ? `00${val}` : val);

export const convertImageFormat = (format: string): string => {
    switch (format) {
        case 'JPEG':
            return 'JPG';
        default:
            return format;
    }
};

export const msToTime = (val: number) => {
    let s = val;
    const ms = s % 1000;
    s = (s - ms) / 1000;
    const sec = s % 60;
    s = (s - sec) / 60;
    const min = s % 60;
    const hrs = (s - min) / 60;

    return `${getValidTime(hrs)}:${getValidTime(min)}:${getValidTime(
        sec
    )}:${getValidMilliSeconds(ms)}`;
};

export const isFieldMissingInArrayOfObject = <
    U extends {[key: string]: any},
    T extends {[key: string]: U}
>(
    object: T,
    field: string
) => {
    const keys = Object.keys(object) as [key: string];

    return Boolean(keys.find((key) => !object[key][field]));
};

export const isFieldInUSMissing = <T extends {[key: string]: any}>(
    object: T,
    field: string
) => {
    return !Boolean(object[field]);
};

export const getFormattedDueDateString = (dueDate: string | null) => {
    const formattedDueDate = moment(dueDate).format('MM/DD/yyyy');
    const formattedDueTime = moment(dueDate).format('hh:mm A');

    return dueDate ? `${formattedDueDate}, ${formattedDueTime}` : '';
};

export const renderAssignedDate = (item: QCJobItem) => {
    const {assignedAt} = item;

    return getFormattedDueDateString(assignedAt);
};

export const handleModalState = wrap(
    (setOpen: (value: boolean) => void, value: boolean) => {
        setOpen(value);
    }
);

export const getYearValues = (startYear: any, endYear: any) => {
    const copyrightYears = [];

    for (let i = startYear; i <= endYear; i++) {
        copyrightYears.push(i);
    }

    return copyrightYears;
};

export const getIsValidDate = (d: Date | null) => {
    // eslint-disable-next-line no-restricted-globals
    return d instanceof Date && !isNaN((d as unknown) as number);
};

export const handleDateChange = curry(
    (publishedDateField: any, isValidDate: any, date: Date | null) => {
        if (getIsValidDate(date) || !date) {
            // if date is empty, date = null (ok for us)
            isValidDate.input.onChange(true);
            publishedDateField.input.onChange(date);
        } else {
            isValidDate.input.onChange(false);
        }
    }
);

export const formatStatusTitle = (status: string) => {
    switch (status) {
        case 'READY_FOR_QC':
            return QCFormattedStatusEnum.ReadyForQc;
        case 'IN_QC':
            return QCFormattedStatusEnum.InQc;
        case 'QC_FAILED':
            return QCFormattedStatusEnum.QcFailed;
        case 'QC_PASSED':
            return QCFormattedStatusEnum.QcPassed;
        default:
            return '';
    }
};

export const formatTestStatusTitle = (status: QcJobTestStatuses) => {
    switch (status) {
        case QcJobTestStatuses.ReadyForQc:
            return QCFormattedStatusEnum.ReadyForQc;
        case QcJobTestStatuses.InQc:
            return QCFormattedStatusEnum.InQc;
        case QcJobTestStatuses.QcFailed:
            return QCFormattedStatusEnum.QcFailed;
        case QcJobTestStatuses.QcPassed:
            return QCFormattedStatusEnum.QcPassed;
        default:
            return '';
    }
};

export const formatTestStatusLabelToCorectEnum = (
    status: QCFormattedStatusEnum
): QcJobTestStatuses => {
    switch (status) {
        case QCFormattedStatusEnum.ReadyForQc:
            return QcJobTestStatuses.ReadyForQc;
        case QCFormattedStatusEnum.InQc:
            return QcJobTestStatuses.InQc;
        case QCFormattedStatusEnum.QcFailed:
            return QcJobTestStatuses.QcFailed;
        case QCFormattedStatusEnum.QcPassed:
            return QcJobTestStatuses.QcPassed;
        default:
            return QcJobTestStatuses.ReadyForQc;
    }
};

export const isEnter = (code: number) => code === 13;

export const isEmptyObj = <T>(obj: T) => {
    for (const x in obj) {
        return false;
    }
    return true;
};

export const isEmptyArr = <T>(arr: T[]): boolean => arr.length === 0;

export const isDefined = <T>(value: T): boolean => value != null;

export const pipe = <T>(...fns: Function[]) => (x: T) => fns.reduce((y, f) => f(y), x);

export const compose = <T>(...fns: Function[]) => (x: T) =>
    fns.reduceRight((y, f) => f(y), x);

export const getFrom = <T>(key: keyof T) => (obj: T) => obj[key];

export const isArray = <T>(arr: T) => isDefined(arr) && Array.isArray(arr);

export const not = (fn: Function) => <T>(...args: T[]) => !fn(...(args as T[]));

export const isNotArray = not(isArray);

export const isType = <T>(type: string) => (value: T) =>
    isDefined(value) && typeof value === type;

export const isObject = <T extends Object>(obj: T) =>
    isType('object')(obj) && isNotArray(obj);

export const ensureObject = <T extends Object>(obj: T) =>
    isObject(obj) ? obj : ({} as T);

export const isEqual = <T>(value1: T) => (value2: T) => value1 === value2;

export const wait = (delay: number) =>
    new Promise((resolve) => setTimeout(resolve, delay));

export const noop = () => {};

export const ternary = <T, U, V>(predicate: V): Function => {
    return function predicated(correct: T, wrong: U) {
        return Boolean(predicate) ? correct : wrong;
    };
};

export const capitalize = (s: string) => {
    return s.charAt(0).toUpperCase() + s.slice(1);
};

const BYTES_SIZES = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
const KILO = 1024;

export function formatBytes(bytes: number, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const dm = decimals < 0 ? 0 : decimals;

    const i = Math.floor(Math.log(bytes) / Math.log(KILO));

    return parseFloat((bytes / KILO ** i).toFixed(dm)) + ' ' + BYTES_SIZES[i];
}

export const identity = <I>(input: I): I => input;

export const head = <T>(arr: T) => {
    if (!Array.isArray(arr)) return;
    return arr[0];
};
